import type {
  SettingAvailability,
  SettingAvailabilityCondition,
  SettingAvailabilityContext,
  SettingAvailabilityEvaluatorCondition,
  SettingAvailabilityEvaluatorDataRequirements,
  SettingAvailabilityEvaluatorId,
  SettingAvailabilityMissingContextBehavior,
  SettingAvailabilityState,
  SettingAvailabilityUnavailableBehavior,
} from "./types";

export const SETTING_AVAILABILITY_UNAVAILABLE_BEHAVIORS = [
  "hidden",
  "visibleDisabled",
  "entryPointOnly",
] as const satisfies readonly SettingAvailabilityUnavailableBehavior[];

type SettingAvailabilityEvaluatorById = {
  [EvaluatorId in SettingAvailabilityEvaluatorId]: (
    condition: Extract<
      SettingAvailabilityEvaluatorCondition,
      { evaluator: EvaluatorId }
    >,
    context: SettingAvailabilityContext,
  ) => boolean;
};

export const SETTING_AVAILABILITY_EVALUATORS: SettingAvailabilityEvaluatorById =
  {
    "team.canUseAi": (_condition, context) => {
      return Object.is(context.evaluatorData?.teamCanUseAi, true);
    },
    "customerWidget.navigationAvailable": (_condition, context) => {
      return Object.is(
        context.evaluatorData?.customerWidgetNavigationAvailable,
        true,
      );
    },
    "customerAccounts.enabled": (_condition, context) => {
      return Object.is(context.evaluatorData?.customerAccountsEnabled, true);
    },
    "returns.enabled": (_condition, context) => {
      return Object.is(context.evaluatorData?.returnsEnabled, true);
    },
    "packageProtection.enabled": (_condition, context) => {
      return Object.is(context.evaluatorData?.packageProtectionEnabled, true);
    },
    "returns.agentReviewEnabled": (_condition, context) => {
      return Object.is(context.evaluatorData?.returnsAgentReviewEnabled, true);
    },
    "team.typeEquals": (condition, context) => {
      return Object.is(context.evaluatorData?.teamType, condition.teamType);
    },
    "featureFlag.enabled": (condition, context) => {
      return Object.is(
        context.evaluatorData?.featureFlagsByKey?.[condition.featureFlag],
        true,
      );
    },
    "integration.connected": (condition, context) => {
      return Object.is(
        context.evaluatorData?.connectedIntegrationsByKey?.[
          condition.integration
        ],
        true,
      );
    },
  };

export function evaluateSettingAvailability({
  availability,
  context,
  missingContextBehavior,
}: {
  availability: SettingAvailability | undefined;
  context?: SettingAvailabilityContext;
  missingContextBehavior: SettingAvailabilityMissingContextBehavior;
}): SettingAvailabilityState {
  if (!availability) {
    return { status: "available" };
  }
  if (!context) {
    return getMissingContextAvailabilityState({
      availability,
      missingContextBehavior,
    });
  }

  const failedConditions = availability.conditions.filter(
    (condition) => !isSettingAvailabilityConditionMet(condition, context),
  );
  if (failedConditions.length === 0) {
    return { status: "available" };
  }

  const unavailableState: SettingAvailabilityState = {
    status: "unavailable",
    behavior: availability.unavailableBehavior,
    failedConditions,
  };
  if (availability.reason) {
    unavailableState.reason = availability.reason;
  }
  return unavailableState;
}

function getMissingContextAvailabilityState({
  availability,
  missingContextBehavior,
}: {
  availability: SettingAvailability;
  missingContextBehavior: SettingAvailabilityMissingContextBehavior;
}): SettingAvailabilityState {
  if (
    missingContextBehavior === "assumeAvailable" ||
    availability.conditions.length === 0
  ) {
    return { status: "available" };
  }

  const unavailableState: SettingAvailabilityState = {
    status: "unavailable",
    behavior: availability.unavailableBehavior,
    failedConditions: availability.conditions,
  };
  if (availability.reason) {
    unavailableState.reason = availability.reason;
  }
  return unavailableState;
}

export function getSettingAvailabilityConditionSettingIds(
  availability: SettingAvailability | undefined,
): readonly string[] {
  if (!availability) {
    return [];
  }

  return [
    ...new Set(
      availability.conditions.flatMap((condition) =>
        condition.type === "settingEquals" ? [condition.settingId] : [],
      ),
    ),
  ];
}

export function getSettingAvailabilityConditionEvaluatorIds(
  availability: SettingAvailability | undefined,
): readonly SettingAvailabilityEvaluatorId[] {
  if (!availability) {
    return [];
  }

  return [
    ...new Set(
      availability.conditions.flatMap((condition) =>
        condition.type === "evaluator" ? [condition.evaluator] : [],
      ),
    ),
  ];
}

export function getSettingAvailabilityEvaluatorDataRequirements(
  availability: SettingAvailability | undefined,
): SettingAvailabilityEvaluatorDataRequirements {
  return getCombinedSettingAvailabilityEvaluatorDataRequirements([
    availability,
  ]);
}

export function getCombinedSettingAvailabilityEvaluatorDataRequirements(
  availabilities: Iterable<SettingAvailability | undefined>,
): SettingAvailabilityEvaluatorDataRequirements {
  const featureFlags = new Set<
    SettingAvailabilityEvaluatorDataRequirements["featureFlags"][number]
  >();
  const integrations = new Set<
    SettingAvailabilityEvaluatorDataRequirements["integrations"][number]
  >();
  let requiresTeamType = false;
  let requiresTeamCanUseAi = false;
  let requiresCustomerWidgetNavigationAvailable = false;
  let requiresCustomerAccountsEnabled = false;
  let requiresReturnsEnabled = false;
  let requiresPackageProtectionEnabled = false;
  let requiresReturnsAgentReviewEnabled = false;

  for (const availability of availabilities) {
    for (const condition of availability?.conditions ?? []) {
      if (condition.type !== "evaluator") {
        continue;
      }

      switch (condition.evaluator) {
        case "team.canUseAi":
          requiresTeamCanUseAi = true;
          break;
        case "customerWidget.navigationAvailable":
          requiresCustomerWidgetNavigationAvailable = true;
          break;
        case "customerAccounts.enabled":
          requiresCustomerAccountsEnabled = true;
          break;
        case "returns.enabled":
          requiresReturnsEnabled = true;
          break;
        case "packageProtection.enabled":
          requiresPackageProtectionEnabled = true;
          break;
        case "returns.agentReviewEnabled":
          requiresReturnsAgentReviewEnabled = true;
          break;
        case "team.typeEquals":
          requiresTeamType = true;
          break;
        case "featureFlag.enabled":
          featureFlags.add(condition.featureFlag);
          break;
        case "integration.connected":
          integrations.add(condition.integration);
          break;
        default:
          assertNever(condition);
      }
    }
  }

  return {
    requiresTeamCanUseAi,
    requiresCustomerWidgetNavigationAvailable,
    requiresCustomerAccountsEnabled,
    requiresReturnsEnabled,
    requiresPackageProtectionEnabled,
    requiresReturnsAgentReviewEnabled,
    requiresTeamType,
    featureFlags: [...featureFlags],
    integrations: [...integrations],
  };
}

export function isSettingAvailabilitySearchVisible(
  availabilityState: SettingAvailabilityState,
): boolean {
  return (
    availabilityState.status === "available" ||
    availabilityState.behavior !== "hidden"
  );
}

export function shouldShowSettingsForAvailability(
  availabilityState: SettingAvailabilityState,
): boolean {
  return (
    availabilityState.status === "available" ||
    availabilityState.behavior === "visibleDisabled"
  );
}

function isSettingAvailabilityConditionMet(
  condition: SettingAvailabilityCondition,
  context: SettingAvailabilityContext,
): boolean {
  switch (condition.type) {
    case "settingEquals":
      return Object.is(
        context.settingValuesById[condition.settingId],
        condition.value,
      );
    case "evaluator":
      return isSettingAvailabilityEvaluatorConditionMet(condition, context);
    default:
      assertNever(condition);
  }
}

function isSettingAvailabilityEvaluatorConditionMet(
  condition: SettingAvailabilityEvaluatorCondition,
  context: SettingAvailabilityContext,
): boolean {
  switch (condition.evaluator) {
    case "team.canUseAi":
      return SETTING_AVAILABILITY_EVALUATORS["team.canUseAi"](
        condition,
        context,
      );
    case "customerWidget.navigationAvailable":
      return SETTING_AVAILABILITY_EVALUATORS[
        "customerWidget.navigationAvailable"
      ](condition, context);
    case "customerAccounts.enabled":
      return SETTING_AVAILABILITY_EVALUATORS["customerAccounts.enabled"](
        condition,
        context,
      );
    case "returns.enabled":
      return SETTING_AVAILABILITY_EVALUATORS["returns.enabled"](
        condition,
        context,
      );
    case "packageProtection.enabled":
      return SETTING_AVAILABILITY_EVALUATORS["packageProtection.enabled"](
        condition,
        context,
      );
    case "returns.agentReviewEnabled":
      return SETTING_AVAILABILITY_EVALUATORS["returns.agentReviewEnabled"](
        condition,
        context,
      );
    case "team.typeEquals":
      return SETTING_AVAILABILITY_EVALUATORS["team.typeEquals"](
        condition,
        context,
      );
    case "featureFlag.enabled":
      return SETTING_AVAILABILITY_EVALUATORS["featureFlag.enabled"](
        condition,
        context,
      );
    case "integration.connected":
      return SETTING_AVAILABILITY_EVALUATORS["integration.connected"](
        condition,
        context,
      );
    default:
      assertNever(condition);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled setting availability condition ${String(value)}`);
}
